import { CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { NovaOrb } from '../components/NovaOrb.js';
import { ApiError } from '../lib/api.js';

const inputClass =
  'border-hairline bg-base/60 text-ink placeholder:text-ink-soft/60 focus:border-accent/50 h-11 w-full rounded-lg border px-3 text-sm transition';

/**
 * Step one of the forgot-password flow: ask for a reset link. The confirmation
 * is deliberately the same whether or not the address has an account — Nova
 * never reveals who is registered.
 */
export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <NovaOrb size={18} />
          <h1 className="font-display text-ink mt-4 text-3xl font-bold tracking-[0.3em]">NOVA</h1>
          <p className="text-ink-soft mt-2 text-sm">Glemt passord</p>
        </div>

        {done ? (
          <div className="glass shadow-panel space-y-4 p-6">
            <div className="text-ink-soft flex items-start gap-3 text-sm">
              <CheckCircle2 size={18} className="text-success mt-0.5 shrink-0" aria-hidden />
              <p>
                Finnes det en konto på denne adressen, har vi sendt en e-post med en lenke for å
                velge nytt passord. Sjekk innboksen — og gjerne søppelposten.
              </p>
            </div>
            <Link
              to="/logg-inn"
              className="text-accent hover:text-accent/80 block text-center text-sm transition"
            >
              Tilbake til innlogging
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass shadow-panel space-y-4 p-6">
            <p className="text-ink-soft text-sm leading-relaxed">
              Skriv inn e-postadressen din, så sender vi en lenke for å velge et nytt passord.
            </p>
            <div>
              <label htmlFor="email" className="label-micro mb-1.5 block">
                E-postadresse
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="fornavn.etternavn@prokom.no"
              />
            </div>

            {error && (
              <p
                role="alert"
                className="border-danger/30 bg-danger/10 text-danger rounded-lg border px-3 py-2 text-sm"
              >
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" loading={busy} className="w-full">
              Send lenke
            </Button>
            <Link
              to="/logg-inn"
              className="text-ink-soft hover:text-ink block text-center text-xs transition"
            >
              Tilbake til innlogging
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}
